import React, { useEffect, useState } from "react";
import { MapPin, Search, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * First-visit location gate. Modal-blocks the entire UI until the visitor
 * either (a) allows browser geolocation, or (b) picks a city from the list.
 * Once a city is selected (or already set), the gate never reappears for that
 * browser session AND the choice is persisted on the user account on the server.
 */
export default function LocationGate() {
  const { city, setCity, ready } = useAuth();
  const [query, setQuery] = useState("");
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);

  // Fetch popular / matching cities
  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get("/cities", { params: { q: query, limit: 60 } })
      .then((r) => { if (active) setCities(r.data.cities || []); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [query]);

  if (!ready || city) return null;

  const useCurrent = () => {
    if (!navigator.geolocation) {
      alert("Your browser doesn't support location access. Please pick a city below.");
      return;
    }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const r = await api.get("/cities/reverse-geocode", { params: { lat: latitude, lon: longitude } });
          if (r.data?.city) {
            setCity(r.data.city);
          } else {
            alert("Couldn't detect your city. Please pick from the list below.");
          }
        } catch (e) {
          alert("Couldn't detect your city. Please pick from the list below.");
        } finally {
          setGeoBusy(false);
        }
      },
      () => {
        setGeoBusy(false);
        alert("Location permission denied. Please pick your city from the list.");
      },
      { timeout: 8000 }
    );
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-background/80 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="location-gate"
    >
      <div
        className="glass w-full max-w-md rounded-none border border-border shadow-2xl max-h-[90vh] overflow-hidden flex flex-col slide-up glow-yellow"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-border relative">
          <div className="relative">
            <div className="eyebrow text-xs text-brand">Welcome to BHEGA</div>
            <h3 className="font-heading font-extrabold text-2xl mt-1 text-foreground leading-tight">Where are you<br/>buying from?</h3>
            <p className="text-sm text-muted-foreground mt-3">
              We unite buyers state-wise to negotiate the best deals. Pick a city to continue.
            </p>
          </div>
        </div>

        <div className="px-6 py-5 space-y-3 overflow-y-auto">
          <button
            onClick={useCurrent}
            disabled={geoBusy}
            className="w-full flex items-center gap-3 p-4 border border-border rounded-none hover:border-[#FFBE06] hover:bg-[#FFBE06]/5 transition-all text-left group"
            data-testid="location-gate-allow"
          >
            <span className="w-11 h-11 bg-[#FFBE06] text-black rounded-full flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
              {geoBusy ? <Loader2 size={16} className="animate-spin" /> : <MapPin size={16} />}
            </span>
            <span className="flex-1">
              <span className="font-bold text-foreground block">Allow current location</span>
              <span className="text-xs text-muted-foreground">Auto-detect your city for the most relevant deals.</span>
            </span>
          </button>

          <div className="eyebrow text-xs pt-2">Or select your city</div>
          <div className="relative">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground z-10" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search city (e.g. Ahmedabad, Mumbai…)"
              className="input-bhega pl-10"
              data-testid="location-gate-search"
            />
          </div>

          <div className="max-h-56 overflow-y-auto border border-border rounded-none divide-y divide-border bg-foreground/[0.02]">
            {loading && <div className="text-center text-xs text-muted-foreground py-6">Loading…</div>}
            {!loading && cities.length === 0 && (
              <div className="text-center text-xs text-muted-foreground py-6">No cities match &ldquo;{query}&rdquo;</div>
            )}
            {cities.map((c) => (
              <button
                key={c}
                onClick={() => setCity(c)}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-foreground/5 hover:text-brand text-foreground/80 transition-colors"
                data-testid={`location-gate-city-${c.split(",")[0].toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
